/**
 * MarketMapPicker — click-to-pin 2GIS MapGL location picker for the market
 * form (SCRUM-158). Mirrors `features/shops/ShopsMap.tsx`'s SDK-loading
 * pattern verbatim (CDN `<script>` append, no inline script — CSP-safe; 2GIS
 * is the ONLY map provider in GardEn, TRD §5.3): the map centers on the
 * market's saved coordinates when editing (or Bishkek for a new market), and
 * clicking anywhere on the map drops/moves a single pin there and reports the
 * new coordinates back to the form via `onPick`.
 *
 * Scoped DOWN from the full ask ("search/drop a pin"): there is no address
 * search here — no geocoding/places-search backend contract exists anywhere
 * in this codebase to build one against (verified: no such route under
 * `/admin/marketplace` or elsewhere), so wiring a real search box would mean
 * inventing an unreviewed API contract. Click-to-pin already satisfies "select
 * a point on a 2GIS map, get lat/long back" — the Lat/Lng `Field`s in
 * `MarketFormDialog` remain fully editable as the manual-entry fallback the
 * bug ticket asks for, independent of this component.
 *
 * One-way data flow: this component reads the form's lat/lng ONCE at mount
 * (to center + seed the initial pin) and never re-reads them afterwards —
 * `MarketFormDialog` already remounts wholesale (a `key` on
 * `MarketListPage`'s `<MarketFormDialog>`, SCRUM-157) whenever a different
 * market is opened, so "mount == fresh market" already holds; live manual
 * edits to the Lat/Lng text fields do not recenter the map, only a map click
 * updates the fields (map -> fields, not fields -> map).
 *
 * When `VITE_2GIS_API_KEY` is not baked into the build (local dev / CI /
 * vitest), the SDK has nothing to authenticate against, so this renders a
 * short explanatory placeholder instead of attempting to load the map —
 * manual Lat/Lng entry is unaffected either way (`ShopsScreen` gates its own
 * `ShopsMap` the same way, via `hasMapKey`).
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { LngLat, MapGLMap, MapGLMarker } from "@/types/2gis";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";

/** 2GIS MapGL CDN entry point (allow-listed in the map CSP). */
const SDK_URL = "https://mapgl.2gis.com/api/js/v1";
/** Bishkek center — same default `ShopsMap` uses for a market-free view. */
const BISHKEK_CENTER: LngLat = [74.6, 42.87];
const CITY_ZOOM = 12;
/** Tighter zoom once a specific market coordinate is known. */
const PIN_ZOOM = 15;

export interface MarketMapPickerProps {
  /** Current Lat/Lng field values (Decimal strings, possibly blank/invalid) —
   * read once at mount to seed the map's center + initial pin. */
  lat: string;
  lng: string;
  /** Fired with fresh Decimal-STRING coordinates (6dp) when the admin clicks
   * the map. The caller is responsible for pushing these into the form. */
  onPick: (lat: string, lng: string) => void;
  height?: number | string;
}

function parseCoord(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** 6dp is sub-meter precision — plenty for a hand-picked map point, and
 * matches the Decimal-string-on-the-wire contract (`api.ts`: lat/lng are
 * forwarded verbatim, never parsed to float, so the STRING shape here is
 * what ultimately reaches the API). */
function toDecimalString(n: number): string {
  return n.toFixed(6);
}

export function MarketMapPicker({
  lat,
  lng,
  onPick,
  height = 260,
}: MarketMapPickerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapGLMap | null>(null);
  const markerRef = useRef<MapGLMarker | null>(null);
  // Keep the latest onPick without re-running the init effect.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const [errored, setErrored] = useState(false);

  const hasKey =
    typeof import.meta.env.VITE_2GIS_API_KEY === "string" &&
    import.meta.env.VITE_2GIS_API_KEY.length > 0;

  useEffect(() => {
    if (!hasKey) return;
    let cancelled = false;
    const apiKey = import.meta.env.VITE_2GIS_API_KEY;

    // Mount-time snapshot only — see the module docstring's "one-way data
    // flow" note for why this effect intentionally has no [lat, lng] deps.
    const initialLat = parseCoord(lat);
    const initialLng = parseCoord(lng);
    const hasInitialPoint = initialLat !== null && initialLng !== null;
    const initialCenter: LngLat = hasInitialPoint
      ? [initialLng as number, initialLat as number]
      : BISHKEK_CENTER;

    const placeMarker = (coords: LngLat): void => {
      const map = mapRef.current;
      const mapgl = window.mapgl;
      if (!map || !mapgl) return;
      markerRef.current?.destroy();
      markerRef.current = new mapgl.Marker(map, { coordinates: coords });
    };

    const initMap = (): void => {
      const mapgl = window.mapgl;
      const container = containerRef.current;
      if (!mapgl || !container || cancelled || mapRef.current) return;
      try {
        const map = new mapgl.Map(container, {
          center: initialCenter,
          zoom: hasInitialPoint ? PIN_ZOOM : CITY_ZOOM,
          ...(typeof apiKey === "string" && apiKey.length > 0 ? { key: apiKey } : {}),
        });
        mapRef.current = map;
        if (hasInitialPoint) placeMarker(initialCenter);
        map.on("click", (event) => {
          if (cancelled) return;
          const coords = event.lngLat;
          placeMarker(coords);
          onPickRef.current(toDecimalString(coords[1]), toDecimalString(coords[0]));
        });
      } catch {
        if (!cancelled) setErrored(true);
      }
    };

    const cleanup = (): void => {
      cancelled = true;
      markerRef.current?.destroy();
      markerRef.current = null;
      mapRef.current?.destroy();
      mapRef.current = null;
    };

    if (window.mapgl) {
      initMap();
      return cleanup;
    }
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (!cancelled) initMap();
    };
    script.onerror = () => {
      if (!cancelled) setErrored(true);
    };
    document.head.appendChild(script);
    return () => {
      script.onload = null;
      script.onerror = null;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey]);

  if (!hasKey) {
    return (
      <div
        data-testid="market-map-picker-unavailable"
        className="flex min-h-[88px] items-center justify-center rounded-[var(--g-radius)] border border-dashed border-[var(--g-hairline)] p-4 text-center text-xs text-[var(--g-secondary)]"
      >
        {t("markets.form.map.unavailable")}
      </div>
    );
  }

  if (errored) {
    return (
      <div
        data-testid="market-map-picker-error"
        role="alert"
        style={{ height }}
        className="grid w-full place-items-center rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] text-center text-sm text-[var(--g-secondary)]"
      >
        {t("markets.form.map.error")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        ref={containerRef}
        data-testid="market-map-picker"
        role="application"
        aria-label={t("markets.form.map.ariaLabel")}
        style={{ height }}
        className="w-full overflow-hidden rounded-[var(--g-radius)] border border-[var(--g-hairline)]"
      />
      <p className="text-xs text-[var(--g-secondary)]">{t("markets.form.map.hint")}</p>
    </div>
  );
}

export default MarketMapPicker;
